import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import Dropdown from '../../components/Dropdown';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TakeActionModal = ({ lead, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [actionOutcome, setActionOutcome] = useState('Completed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionDate, setActionDate] = useState(null);
  const [actionTime, setActionTime] = useState('');
  const [actionDaysToAdd, setActionDaysToAdd] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [nextStep, setNextStep] = useState('Complete');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [executiveSchedule, setExecutiveSchedule] = useState({ followups: [], deliveries: [] });
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);
  const nextStepOptions = React.useMemo(() => {
    if (!statuses || statuses.length === 0) {
      return [];
    }
    const sorted = [...statuses].sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 0;
      const orderB = typeof b.order === 'number' ? b.order : 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.id || 0) - (b.id || 0);
    });
    return sorted.map(s => ({ value: s.name, label: s.name }));
  }, [statuses]);

  useEffect(() => {
    api.get('/masters/status').then(res => {
      const active = (res.data || []).filter(s => s.status === 'Active');
      active.sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : 0;
        const orderB = typeof b.order === 'number' ? b.order : 0;
        if (orderA !== orderB) return orderA - orderB;
        return (a.id || 0) - (b.id || 0);
      });
      setStatuses(active);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!actionDate || !lead) {
        setExecutiveSchedule({ followups: [], deliveries: [] });
        return;
      }
      const assigneeId = lead.userId || (lead.user && lead.user.id);
      if (!assigneeId) return;

      setIsFetchingSchedule(true);
      try {
        const d = new Date(actionDate);
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const res = await api.get(`/users/${assigneeId}/schedule?date=${dateString}&_t=${Date.now()}`);
        setExecutiveSchedule(res.data);
      } catch (err) {
        console.error('Failed to fetch schedule', err);
        setExecutiveSchedule({ followups: [], deliveries: [] });
      } finally {
        setIsFetchingSchedule(false);
      }
    };
    fetchSchedule();
  }, [actionDate, lead]);

  const dropdownOptions = React.useMemo(() => {
    const statusStr = typeof lead?.status === 'string' ? lead.status : (lead?.status?.name || '');
    const statusLower = statusStr.toLowerCase().trim();
    const isInactiveState = ['on hold', 'hold', 'complete', 'completed', 'won', 'lost', 'cancelled', 'cancel', 'closed'].includes(statusLower) || lead?.statusId === 6 || lead?.statusId === 4 || lead?.statusId === 5;

    let isFollowUp = false;
    let isOfficeVisit = false;

    if (!isInactiveState) {
      isFollowUp = statusLower.includes('follow');
      isOfficeVisit = statusLower.includes('office visit') || statusLower.includes('visit');

      if (!isFollowUp && !isOfficeVisit && lead?.followups && lead.followups.length > 0) {
        const activeFollowup = lead.followups.find(f => {
          if (f.type === 'System') return false;
          const t = (f.type || '').toLowerCase();
          const n = f.notes || '';
          if (t.includes('completed') || t.includes('cancelled') || t.includes('closed')) return false;
          if (n.startsWith('[Closed]') || n.startsWith('[Completed]') || n.startsWith('[Complete') || n.startsWith('[Cancel') || n.startsWith('[Status Change')) return false;
          return true;
        });
        if (activeFollowup) {
          const t = (activeFollowup.type || '').toLowerCase();
          const n = (activeFollowup.notes || '').toLowerCase();
          if (t.includes('visit') || n.includes('visit')) {
            isOfficeVisit = true;
          } else if (t.includes('follow') || n.includes('follow')) {
            isFollowUp = true;
          }
        }
      }
    }

    if (isFollowUp) {
      return [
        { value: 'Complete Follow Up', label: 'Complete Follow Up' },
        { value: 'Cancel Follow Up', label: 'Cancel Follow Up' },
      ];
    } else if (isOfficeVisit) {
      return [
        { value: 'Complete Office Visit', label: 'Complete Office Visit' },
        { value: 'Cancel Office Visit', label: 'Cancel Office Visit' },
      ];
    }

    const sorted = [...statuses].sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 0;
      const orderB = typeof b.order === 'number' ? b.order : 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.id || 0) - (b.id || 0);
    });
    return sorted.map(s => ({ value: s.name, label: s.name }));
  }, [statuses, lead]);

  const isComplete = 
    actionOutcome === 'Complete Follow Up' ||
    actionOutcome === 'Complete Office Visit' ||
    actionOutcome === 'Complete Visit';

  const isCancel = 
    actionOutcome === 'Cancel Follow Up' ||
    actionOutcome === 'Cancel Office Visit' ||
    actionOutcome === 'Cancel Visit';

  const isCompleteOrCancel = isComplete || isCancel;

  const isNextStepRequiringDate = 
    nextStep?.toLowerCase()?.includes('follow') ||
    nextStep?.toLowerCase()?.includes('visit') ||
    nextStep?.toLowerCase()?.includes('schedule') ||
    nextStep?.toLowerCase()?.includes('reschedule');

  const requiresDate = isCompleteOrCancel
    ? isNextStepRequiringDate
    : (actionOutcome?.toLowerCase()?.includes('schedule') || actionOutcome?.toLowerCase()?.includes('follow') || actionOutcome?.toLowerCase()?.includes('visit'));

  const handleActionOutcomeChange = (val) => {
    setActionOutcome(val);
    if (val.startsWith('Complete')) {
      setNextStep('Complete');
    } else if (val.startsWith('Cancel')) {
      const cancelOption = statuses.find(s => s.name.toLowerCase() === 'cancelled') || statuses.find(s => s.name.toLowerCase() === 'on hold');
      setNextStep(cancelOption ? cancelOption.name : 'On Hold');
    }
  };

  const initializedRef = React.useRef(false);

  useEffect(() => {
    initializedRef.current = false;
  }, [lead?.id]);

  useEffect(() => {
    if (dropdownOptions.length > 0) {
      const statusStr = typeof lead?.status === 'string' ? lead.status : (lead?.status?.name || '');
      const statusLower = statusStr.toLowerCase().trim();
      const currentStatusOpt = dropdownOptions.find(o => o.value.toLowerCase() === statusLower);

      if (!initializedRef.current) {
        initializedRef.current = true;
        let defaultChoice;
        if (dropdownOptions.some(o => o.value === 'Complete Follow Up')) {
          defaultChoice = 'Complete Follow Up';
          setNextStep('Complete');
        } else if (dropdownOptions.some(o => o.value === 'Complete Office Visit')) {
          defaultChoice = 'Complete Office Visit';
          setNextStep('Complete');
        } else if (currentStatusOpt) {
          defaultChoice = currentStatusOpt.value;
        } else {
          defaultChoice = dropdownOptions[0].value;
        }
        setActionOutcome(defaultChoice);
      } else if (!dropdownOptions.find(o => o.value === actionOutcome)) {
        let fallbackChoice = currentStatusOpt ? currentStatusOpt.value : dropdownOptions[0].value;
        setActionOutcome(fallbackChoice);
        if (fallbackChoice.startsWith('Complete')) setNextStep('Complete');
        else if (fallbackChoice.startsWith('Cancel')) {
          const cancelOption = statuses.find(s => s.name.toLowerCase() === 'cancelled') || statuses.find(s => s.name.toLowerCase() === 'on hold');
          setNextStep(cancelOption ? cancelOption.name : 'On Hold');
        }
      }
    }
  }, [dropdownOptions, statuses, lead]);

  const handleActionDaysChange = (val) => {
    setActionDaysToAdd(val);
    if (val !== '' && val !== null && val !== undefined && !isNaN(Number(val))) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(val, 10));
      setActionDate(d);
    } else {
      setActionDate(null);
    }
  };

  const handleActionDateChange = (date) => {
    setActionDate(date);
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sel = new Date(date);
      sel.setHours(0, 0, 0, 0);
      const diffDays = Math.round((sel - today) / (1000 * 60 * 60 * 24));
      setActionDaysToAdd(diffDays >= 0 ? diffDays.toString() : '0');
    } else {
      setActionDaysToAdd('');
    }
  };

  const getTimeDate = (timeStr) => {
    if (!timeStr) return null;
    const str = timeStr.toString().trim();
    const [timePart, modifier] = str.split(/\s+/);
    const parts = timePart.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1] || '0', 10);
    if (isNaN(hours)) return null;
    if (modifier) {
      if (modifier.toLowerCase() === 'pm' && hours < 12) hours += 12;
      if (modifier.toLowerCase() === 'am' && hours === 12) hours = 0;
    }
    const d = new Date();
    d.setHours(hours, isNaN(minutes) ? 0 : minutes, 0, 0);
    return d;
  };

  const getFollowupType = (outcome, nextStep) => {
    if (!outcome) return 'Activity';
    if (outcome === 'Complete Follow Up') return 'Follow-up Completed';
    if (outcome === 'Complete Office Visit' || outcome === 'Complete Visit') return 'Visit Completed';
    if (outcome === 'Cancel Follow Up' || outcome === 'Cancel Office Visit' || outcome === 'Cancel Visit') return 'Follow-up Cancelled';
    if ((outcome || '').toLowerCase().includes('reschedule') || (nextStep || '').toLowerCase() === 'reschedule') return 'Follow-up Rescheduled';
    if ((outcome || '').toLowerCase().includes('visit') || (nextStep || '').toLowerCase() === 'visit') return 'Office Visit Scheduled';
    if ((outcome || '').toLowerCase().includes('schedule') || (nextStep || '').toLowerCase() === 'schedule' || (outcome || '').toLowerCase().includes('follow') || (nextStep || '').toLowerCase() === 'follow') return 'Follow-up Scheduled';
    if (['won', 'completed', 'complete'].includes(outcome.toLowerCase())) return 'Lead is Complete';
    return outcome;
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!lead) {
      setIsSubmitting(false);
      return;
    }

    if (requiresDate && (!actionDate || !actionTime)) {
      toast.error("Please select a date and time for the follow-up");
      setIsSubmitting(false);
      return;
    }

    try {
      // Close all pending/active followups for this lead
      // Close only actual pending/scheduled followups for this lead
      if (lead.followups && lead.followups.length > 0) {
        const pendingToClose = lead.followups.filter(f => {
          if (!f || !f.id) return false;
          if (f.type === 'System') return false;
          const fn = f.notes || '';
          const ft = (f.type || '').toLowerCase();
          const fnLower = fn.toLowerCase();
          // Never touch completed, cancelled, closed, or system records
          if (fn.startsWith('[Closed]') || fn.startsWith('[Completed]') || fn.startsWith('[Complete') || fn.startsWith('[Cancel')) return false;
          if (ft.includes('complete') || ft.includes('cancel') || ft.includes('closed')) return false;
          if (fnLower.startsWith('visit started') || ft === 'visit started') return false;
          return true;
        });

        for (const f of pendingToClose) {
          await api.put(`/followups/${f.id}`, {
            notes: f.notes?.startsWith('[Closed]') ? f.notes : `[Closed] ${f.notes || ''}`.trim(),
            type: f.type,
            date: f.date
          });
        }
      }

      // If completing or cancelling, log the activity immediately
      if (isCompleteOrCancel) {
        await api.post('/followups', {
          leadId: lead.id,
          date: new Date().toISOString(),
          notes: actionNotes ? `[${actionOutcome}] ${actionNotes}` : `[${actionOutcome}]`,
          type: getFollowupType(actionOutcome, nextStep),
        });
      } else if (!requiresDate) {
        await api.post('/followups', {
          leadId: lead.id,
          date: new Date().toISOString(),
          notes: actionNotes || '',
          type: getFollowupType(actionOutcome, nextStep),
        });
      }

      const finalOutcome = isCompleteOrCancel ? nextStep : actionOutcome;

      // If scheduling follow-up / visit with future date
      if (requiresDate) {
        const d = new Date(actionDate);
        const [hours, minutes] = actionTime.split(':');
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        let scheduledType = 'Follow-up Scheduled';
        if ((finalOutcome || '').toLowerCase().includes('visit')) scheduledType = 'Office Visit Scheduled';
        else if ((finalOutcome || '').toLowerCase().includes('resched')) scheduledType = 'Follow-up Rescheduled';

        await api.post('/followups', {
          leadId: lead.id,
          date: d.toISOString(),
          notes: isComplete ? '' : (actionNotes || ''),
          type: scheduledType,
        });
      }

      const targetStatus = statuses.find(s => s.name.toLowerCase() === finalOutcome.toLowerCase()) ||
        statuses.find(s => s.name.toLowerCase().includes(finalOutcome.toLowerCase()));

      if (targetStatus) {
        const isConverted = ['won', 'completed', 'complete'].includes(targetStatus.name.toLowerCase());
        await api.put(`/leads/${lead.id}`, {
          statusId: targetStatus.id,
          isCustomer: isConverted,
          skipAutoCancel: true
        });

        // Log status change activity if moving to a status that doesn't have a future scheduled date
        const currentStatusName = typeof lead?.status === 'string' ? lead.status : (lead?.status?.name || '');
        if (targetStatus.name.toLowerCase() !== currentStatusName.toLowerCase() && !requiresDate) {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date(Date.now() + 1000).toISOString(),
            notes: `[Status Change] Status changed to ${targetStatus.name}`,
            type: 'System',
          });
        }

        if (isConverted && lead.status !== targetStatus.name) {
          const { notifyAdmins } = await import('../../utils/notify');
          await notifyAdmins(`Lead #${lead.id} (${lead.name || lead.companyName}) was converted to a customer by ${user.name}.`);
        }
      }

      toast.success("Action saved successfully");
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to save action");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 2000, padding: '0.75rem' }}>
      <div className="modal-content fade-in" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '680px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', maxHeight: '94vh' }}>
        <div className="modal-header" style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>Take Action</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleActionSubmit} style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.825rem' }}>Action Outcome</label>
            <Dropdown
              style={{ width: '100%', borderRadius: '8px' }}
              value={actionOutcome}
              onChange={handleActionOutcomeChange}
              options={dropdownOptions}
            />
          </div>

          {isCompleteOrCancel && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: '#0369a1', fontSize: '0.825rem' }}>
                {isCancel
                  ? 'Status After Cancellation'
                  : (actionOutcome.includes('Follow Up') ? 'Next Step (After Follow Up)' : 'Next Step (After Office Visit)')}
              </label>
              <Dropdown
                style={{ width: '100%', borderRadius: '8px' }}
                value={nextStep}
                onChange={(val) => setNextStep(val)}
                options={nextStepOptions}
              />
            </div>
          )}

          {requiresDate && (
            <div
              style={{
                maxWidth: '100%',
                padding: '1.1rem 1.25rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.85rem'
              }}
            >
              <div className="form-grid-3" style={{ gap: '0.85rem 0.75rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: '#0369a1', fontSize: '0.825rem' }}>
                    {(nextStep || actionOutcome || '').toLowerCase().includes('visit') ? 'Visit In (Days)' : 'Follow-up In (Days)'} <span style={{ color: 'var(--color-danger)' }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="input-field w-full"
                    style={{ backgroundColor: 'white' }}
                    placeholder="e.g. 15"
                    min="0"
                    value={actionDaysToAdd}
                    onChange={(e) => handleActionDaysChange(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: '#0369a1', fontSize: '0.825rem' }}>
                    {(nextStep || actionOutcome || '').toLowerCase().includes('visit') ? 'Calculated Visit Date' : 'Calculated Follow-up Date'}
                  </label>
                  <DatePicker
                    selected={actionDate}
                    onChange={handleActionDateChange}
                    className="input-field w-full"
                    wrapperClassName="w-full"
                    style={{ backgroundColor: 'white', width: '100%' }}
                    placeholderText="Select date..."
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: '#0369a1', fontSize: '0.825rem' }}>
                    {(nextStep || actionOutcome || '').toLowerCase().includes('visit') ? 'Visit Time' : 'Follow-up Time'} <span style={{ color: 'var(--color-danger)' }}>*</span>
                  </label>
                  <DatePicker
                    wrapperClassName="w-full"
                    selected={getTimeDate(actionTime)}
                    onChange={(date) => {
                      if (date) {
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        setActionTime(`${hours}:${minutes}`);
                      } else {
                        setActionTime('');
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="input-field w-full"
                    style={{ backgroundColor: 'white' }}
                    placeholderText="Select time..."
                    required
                  />
                </div>
              </div>

              {/* Executive Schedule Preview */}
              {actionDate && (
                <div
                  style={{
                    padding: '0.75rem 0.85rem',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                    marginTop: '0.25rem'
                  }}
                >
                  <h4 style={{ fontSize: '0.8rem', fontWeight: '600', color: '#0369a1', margin: '0 0 0.4rem 0' }}>
                    Executive's Schedule for {new Date(actionDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </h4>
                  {isFetchingSchedule ? (
                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Loader2 className="animate-spin" size={14} /> Loading schedule...
                    </div>
                  ) : (executiveSchedule.followups?.length === 0 && executiveSchedule.deliveries?.length === 0) ? (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      No scheduled events for this day. Free to schedule!
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: '140px', overflowY: 'auto', paddingRight: '0.25rem' }}>
                      {executiveSchedule.followups?.map((f, i) => (
                        <div key={`f-${i}`} style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{f.lead?.companyName || f.lead?.contactPerson || 'Lead'}</span>
                          <span style={{ color: '#64748b', fontWeight: '500' }}>{new Date(f.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                      {executiveSchedule.deliveries?.map((d, i) => (
                        <div key={`d-${i}`} style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{d.title || d.partyName || 'Order'}</span>
                          <span style={{ color: '#64748b', fontWeight: '500' }}>{new Date(d.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.825rem' }}>Notes (Optional)</label>
            <textarea
              className="input-field w-full"
              rows="3"
              placeholder="Summary of interaction..."
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              style={{ resize: 'none', borderRadius: '8px', minHeight: '64px', fontSize: '0.85rem', padding: '0.6rem 0.75rem' }}
            ></textarea>
          </div>
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.25rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.5rem 1.25rem', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'white', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', border: 'none', borderRadius: '8px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}>
              {isSubmitting && <Loader2 size={15} className="spinner-icon" />}
              {isSubmitting ? 'Saving...' : 'Save Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TakeActionModal;
